import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  ArrowLeft,
  Save,
  RotateCcw,
  MapPin,
  Car,
  Clock,
  Calendar,
  TrendingUp,
  DollarSign,
  Zap,
  CheckCircle,
  AlertCircle,
  Loader2,
} from "lucide-react";
import { useSurgePricing, useUpdateSurgePricing } from "@/api-services/hooks/useSurgePricing";
import { useCities, useVehicleCategories } from "@/api-services/hooks/usePricing";
import { SurgePricing, SurgePricingForm } from "@/api-services/hooks/surgePricingTypes";
import { CityMaster, VehicleCategory } from "@/api-services/hooks/pricingTypes";
import { getStoredCurrency } from "@/api-services/hooks/useSettings";

const EditSurgePricingPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const surgePricingId = parseInt(id || "0");

  // Form state
  const [formData, setFormData] = useState<SurgePricingForm>({
    vehicleCategoryId: 0,
    cityId: 0,
    startTime: "09:00",
    endTime: "18:00",
    surgeMultiplier: 1.5,
    dayOfWeek: "MONDAY",
    maxSurgeCap: 50.0,
    isActive: true,
  });

  // Validation state
  const [errors, setErrors] = useState<Partial<Record<keyof SurgePricingForm, string>>>({});
  const [isDirty, setIsDirty] = useState(false);
  const [showLeaveModal, setShowLeaveModal] = useState(false);

  // Format time from LocalTime object or string to HH:MM format
  const formatTimeForInput = useCallback((time: { hour: number; minute: number } | string) => {
    if (typeof time === 'string') {
      const [hours, minutes] = time.split(':');
      return `${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}`;
    } else if (time && typeof time === 'object' && 'hour' in time && 'minute' in time) {
      const hour = time.hour.toString().padStart(2, "0");
      const minute = time.minute.toString().padStart(2, "0");
      return `${hour}:${minute}`;
    }
    return '09:00';
  }, []);

  // Fetch existing surge pricing data
  const { data: existingData, loading, error, refetch } = useSurgePricing(
    surgePricingId,
    {
      enabled: !!surgePricingId && surgePricingId > 0,
      onError: (err) => {
        toast.error(`Failed to load surge pricing data: ${err.message}`);
      },
    }
  );

  // Fetch dropdown data
  const { data: cities } = useCities({ enabled: true });
  const { data: vehicleCategories } = useVehicleCategories({ enabled: true });

  // Update mutation
  const { mutate: updateSurgePricing, isLoading: isUpdating } = useUpdateSurgePricing({
    onSuccess: () => {
      toast.success("Surge pricing rule updated successfully!");
      navigate("/admin/surge-pricing");
    },
    onError: (error) => {
      toast.error(`Failed to update surge pricing: ${error.message}`);
    },
  });

  // Initialize form with existing data
  useEffect(() => {
    if (existingData) {
      const formData: SurgePricingForm = {
        vehicleCategoryId: existingData.vehicleCategoryId || 0,
        cityId: existingData.city?.cityId || 0,
        startTime: formatTimeForInput(existingData.startTime),
        endTime: formatTimeForInput(existingData.endTime),
        surgeMultiplier: existingData.surgeMultiplier,
        dayOfWeek: existingData.dayOfWeek,
        maxSurgeCap: existingData.maxSurgeCap,
        isActive: existingData.isActive,
      };

      setFormData(formData);
      setIsDirty(false); // Reset dirty state when data is loaded
    }
  }, [existingData, formatTimeForInput, surgePricingId]);

  // Validation
  const validateForm = useCallback((): boolean => {
    const newErrors: Partial<Record<keyof SurgePricingForm, string>> = {};

    if (!formData.vehicleCategoryId) {
      newErrors.vehicleCategoryId = "Vehicle category is required";
    }

    if (!formData.cityId) {
      newErrors.cityId = "City is required";
    }

    if (!formData.startTime) {
      newErrors.startTime = "Start time is required";
    }

    if (!formData.endTime) {
      newErrors.endTime = "End time is required";
    }

    if (formData.startTime && formData.endTime && formData.startTime >= formData.endTime) {
      newErrors.endTime = "End time must be after start time";
    }

    if (!formData.surgeMultiplier || formData.surgeMultiplier <= 0) {
      newErrors.surgeMultiplier = "Surge multiplier must be greater than 0";
    }

    if (!formData.maxSurgeCap || formData.maxSurgeCap <= 0) {
      newErrors.maxSurgeCap = "Maximum surge cap must be greater than 0";
    }

    if (formData.surgeMultiplier > 10) {
      newErrors.surgeMultiplier = "Surge multiplier cannot exceed 10x";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  // Handle form input changes
  const handleInputChange = useCallback((field: keyof SurgePricingForm, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: undefined }));
    setIsDirty(true);
  }, []);

  // Handle form submission
  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Please fix the validation errors");
      return;
    }

    // Find selected city for API
    const selectedCity = cities?.find(city => city.cityId === formData.cityId);

    if (!selectedCity) {
      toast.error("Selected city not found. Please select a valid city.");
      return;
    }

    updateSurgePricing({
      id: surgePricingId,
      data: {
        vehicleCategoryId: formData.vehicleCategoryId,
        city: {
          cityId: selectedCity.cityId // Send simple city object with just cityId
        },
        startTime: formData.startTime + ":00", // Convert to HH:MM:00 format
        endTime: formData.endTime + ":00", // Convert to HH:MM:00 format
        surgeMultiplier: formData.surgeMultiplier,
        dayOfWeek: formData.dayOfWeek,
        maxSurgeCap: formData.maxSurgeCap,
        isActive: formData.isActive,
      },
    });
  }, [formData, surgePricingId, validateForm, updateSurgePricing]);

  // Reset form to original values
  const handleReset = useCallback(() => {
    if (existingData) {
      const formData: SurgePricingForm = {
        vehicleCategoryId: existingData.vehicleCategoryId || 0,
        cityId: existingData.city?.cityId || 0,
        startTime: formatTimeForInput(existingData.startTime),
        endTime: formatTimeForInput(existingData.endTime),
        surgeMultiplier: existingData.surgeMultiplier,
        dayOfWeek: existingData.dayOfWeek,
        maxSurgeCap: existingData.maxSurgeCap,
        isActive: existingData.isActive,
      };
      setFormData(formData);
      setErrors({});
      setIsDirty(false);
    }
  }, [existingData, formatTimeForInput]);

  // Handle navigation
  const handleBack = useCallback(() => {
    if (isDirty) {
      setShowLeaveModal(true);
    } else {
      navigate("/admin/surge-pricing");
    }
  }, [isDirty, navigate]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading surge pricing data...</p>
        </div>
      </div>
    );
  }

  if (!loading && !existingData && !error) return null;

  if (!loading && (error || !existingData)) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Card className="max-w-md">
          <CardContent className="p-6 text-center">
            <AlertCircle className="w-12 h-12 text-destructive mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">
              Error Loading Surge Pricing
            </h3>
            <p className="text-muted-foreground mb-4">
              {error?.message || "Surge pricing not found"}
            </p>
            <Button onClick={() => navigate("/admin/surge-pricing")}>
              Back to List
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Unsaved Changes Modal */}
      <Dialog open={showLeaveModal} onOpenChange={setShowLeaveModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Unsaved Changes</DialogTitle>
            <DialogDescription>
              You have unsaved changes. Are you sure you want to leave?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLeaveModal(false)}>
              Stay on Page
            </Button>
            <Button variant="destructive" onClick={() => navigate("/admin/surge-pricing")}>
              Leave Without Saving
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="sm" onClick={handleBack}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Surge Pricing
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-foreground">
              Edit Surge Pricing Rule
            </h1>
            <p className="text-muted-foreground mt-1">
              Modify the surge pricing rule details
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={handleReset}
            disabled={!isDirty || isUpdating}
          >
            <RotateCcw className="w-4 h-4 mr-2" />
            Reset
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!isDirty || isUpdating}
          >
            {isUpdating ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Updating...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Update Surge Rule
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Basic Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Zap className="w-5 h-5" />
                Basic Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* City Selection */}
              <div className="space-y-2">
                <Label htmlFor="cityId" className="flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  City *
                </Label>
                <Select
                  value={formData.cityId.toString()}
                  onValueChange={(value) => handleInputChange("cityId", parseInt(value))}
                >
                  <SelectTrigger className={errors.cityId ? "border-red-500" : ""}>
                    <SelectValue placeholder="Select a city" />
                  </SelectTrigger>
                  <SelectContent>
                    {cities?.map((city: CityMaster) => (
                      <SelectItem key={city.cityId} value={city.cityId.toString()}>
                        {city.cityName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.cityId && (
                  <p className="text-sm text-red-500">{errors.cityId}</p>
                )}
              </div>

              {/* Vehicle Category Selection */}
              <div className="space-y-2">
                <Label htmlFor="vehicleCategoryId" className="flex items-center gap-2">
                  <Car className="w-4 h-4" />
                  Vehicle Category *
                </Label>
                <Select
                  value={formData.vehicleCategoryId.toString()}
                  onValueChange={(value) => handleInputChange("vehicleCategoryId", parseInt(value))}
                >
                  <SelectTrigger className={errors.vehicleCategoryId ? "border-red-500" : ""}>
                    <SelectValue placeholder="Select a vehicle category" />
                  </SelectTrigger>
                  <SelectContent>
                    {vehicleCategories?.map((category: VehicleCategory) => (
                      <SelectItem key={category.vehicleCategoryId} value={category.vehicleCategoryId.toString()}>
                        {category.vehicleCategoryName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.vehicleCategoryId && (
                  <p className="text-sm text-red-500">{errors.vehicleCategoryId}</p>
                )}
              </div>

              {/* Day of Week */}
              <div className="space-y-2">
                <Label htmlFor="dayOfWeek" className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Day of Week *
                </Label>
                <Select
                  value={formData.dayOfWeek}
                  onValueChange={(value) => handleInputChange("dayOfWeek", value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select day" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="MONDAY">Monday</SelectItem>
                    <SelectItem value="TUESDAY">Tuesday</SelectItem>
                    <SelectItem value="WEDNESDAY">Wednesday</SelectItem>
                    <SelectItem value="THURSDAY">Thursday</SelectItem>
                    <SelectItem value="FRIDAY">Friday</SelectItem>
                    <SelectItem value="SATURDAY">Saturday</SelectItem>
                    <SelectItem value="SUNDAY">Sunday</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Schedule Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="w-5 h-5" />
                Schedule Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Start Time */}
              <div className="space-y-2">
                <Label htmlFor="startTime">Start Time *</Label>
                <Input
                  id="startTime"
                  type="time"
                  value={formData.startTime}
                  onChange={(e) => handleInputChange("startTime", e.target.value)}
                  className={errors.startTime ? "border-red-500" : ""}
                />
                {errors.startTime && (
                  <p className="text-sm text-red-500">{errors.startTime}</p>
                )}
              </div>

              {/* End Time */}
              <div className="space-y-2">
                <Label htmlFor="endTime">End Time *</Label>
                <Input
                  id="endTime"
                  type="time"
                  value={formData.endTime}
                  onChange={(e) => handleInputChange("endTime", e.target.value)}
                  className={errors.endTime ? "border-red-500" : ""}
                />
                {errors.endTime && (
                  <p className="text-sm text-red-500">{errors.endTime}</p>
                )}
              </div>

              {/* Active Status */}
              <div className="flex items-center space-x-2 pt-4">
                <Checkbox
                  id="isActive"
                  checked={formData.isActive}
                  onCheckedChange={(checked) => handleInputChange("isActive", checked)}
                />
                <Label htmlFor="isActive" className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" />
                  Active
                </Label>
              </div>
            </CardContent>
          </Card>

          {/* Pricing Information */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                {/* <DollarSign className="w-5 h-5" /> */}
                {getStoredCurrency()} {" "}
                Pricing Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Surge Multiplier */}
                <div className="space-y-2">
                  <Label htmlFor="surgeMultiplier" className="flex items-center gap-2">
                    <TrendingUp className="w-4 h-4" />
                    Surge Multiplier *
                  </Label>
                  <Input
                    id="surgeMultiplier"
                    type="number"
                    step="0.1"
                    min="0.1"
                    max="10"
                    value={formData.surgeMultiplier}
                    onChange={(e) => handleInputChange("surgeMultiplier", parseFloat(e.target.value) || 0)}
                    className={errors.surgeMultiplier ? "border-red-500" : ""}
                  />
                  {errors.surgeMultiplier && (
                    <p className="text-sm text-red-500">{errors.surgeMultiplier}</p>
                  )}
                  <p className="text-sm text-muted-foreground">
                    Multiplier applied to base pricing during surge hours (e.g., 1.5 for 50% increase)
                  </p>
                </div>

                {/* Maximum Surge Cap */}
                <div className="space-y-2">
                  <Label htmlFor="maxSurgeCap" className="flex items-center gap-2">
                    {/* <DollarSign className="w-4 h-4" /> */}
                    ({getStoredCurrency()}) {" "}
                    Maximum Surge Cap *
                  </Label>
                  <Input
                    id="maxSurgeCap"
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={formData.maxSurgeCap}
                    onChange={(e) => handleInputChange("maxSurgeCap", parseFloat(e.target.value) || 0)}
                    className={errors.maxSurgeCap ? "border-red-500" : ""}
                  />
                  {errors.maxSurgeCap && (
                    <p className="text-sm text-red-500">{errors.maxSurgeCap}</p>
                  )}
                  <p className="text-sm text-muted-foreground">
                    Maximum fare amount during surge pricing
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </form>
    </div>
  );
};

export default EditSurgePricingPage;